import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { fmtNumber } from "../../lib/format";
import { useMaterialRequests, useSupplierDirectory, type MaterialRequest } from "../../lib/procure";
import { Card, ErrorBox, Skeleton } from "../ui";

/** Purchase demand nobody has taken up yet — the warehouse's *Raise the request* drafts among it. *Raise a sourcing
 * request* opens the request form on it; the material request is submitted when that request is approved. */
export function WaitingMaterialRequests({ onRaise }: { onRaise: (mr: MaterialRequest) => void }) {
  const { t, i18n } = useTranslation();
  const q = useMaterialRequests();
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!q.data) return <Skeleton className="h-16" />;
  if (q.data.length === 0) return null;
  return (
    <Card className="overflow-hidden" data-p2p-mr-list={q.data.length}>
      <div className="border-b border-brand-grey-light px-4 py-2"><h2 className="font-heading text-base font-semibold">{t("p2p.demand.title")}</h2><p className="text-xs text-brand-grey">{t("p2p.demand.hint")}</p></div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-brand-grey-light">
          {q.data.map((mr) => (
            <tr key={mr.name} data-p2p-mr={mr.name}>
              <td className="px-4 py-2"><div className="font-semibold">{mr.name} <span className="text-xs font-normal text-brand-grey">{mr.docstatus === 0 ? t("p2p.demand.draft") : mr.status}</span></div><div className="text-xs text-brand-grey">{mr.company} · {mr.owner} · {t("p2p.neededBy")} {mr.schedule_date}</div></td>
              <td className="px-4 py-2 text-xs">{mr.items.map((it) => `${it.item_name || it.item_code} × ${fmtNumber(it.qty, i18n.language, 0)} ${it.uom}`).join(" · ")}</td>
              <td className="px-4 py-2 text-right"><button onClick={() => onRaise(mr)} className="rounded border border-brand-primary px-3 py-1 text-xs font-semibold text-brand-primary hover:bg-brand-primary/5" data-p2p-mr-raise={mr.name}>{t("p2p.demand.raise")}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/** Every supplier, with its qualifications and any file still open — the list procurement never had in OneSphere. */
export function SupplierDirectory() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const q = useSupplierDirectory(search);
  return (
    <Card className="overflow-hidden" data-p2p-directory>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-grey-light px-4 py-2">
        <div><h2 className="font-heading text-base font-semibold">{t("p2p.directory.title")}</h2><p className="text-xs text-brand-grey">{t("p2p.directory.hint")}</p></div>
        <div className="flex items-center gap-2">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("p2p.directory.search")} className="rounded border border-brand-grey-light px-2 py-1 text-sm" data-p2p-directory-search />
          <Link to="/suppliers/new?from=/procure%3Ftab%3Dsuppliers" className="rounded bg-brand-primary px-3 py-1 text-xs font-semibold text-white" data-p2p-new-supplier>{t("p2p.directory.new")}</Link>
        </div>
      </div>
      {q.isError && <div className="p-3"><ErrorBox error={q.error} /></div>}
      {!q.data ? <Skeleton className="h-32" /> : (
        <table className="w-full text-sm">
          <thead className="bg-brand-grey-light/40 text-left text-[11px] uppercase tracking-wide text-brand-grey"><tr><th className="px-4 py-2">{t("p2p.supplierName")}</th><th className="px-4 py-2">{t("p2p.group")}</th><th className="px-4 py-2">{t("p2p.country")}</th><th className="px-4 py-2">{t("p2p.qualification")}</th><th className="px-4 py-2">{t("common.status")}</th></tr></thead>
          <tbody className="divide-y divide-brand-grey-light">
            {q.data.map((s) => (
              <tr key={s.name} data-p2p-directory-row={s.name}>
                <td className="px-4 py-2"><div className="font-semibold">{s.supplier_name || s.name}</div><div className="text-xs text-brand-grey">{s.default_currency}{s.tax_id ? ` · ${s.tax_id}` : ""}</div></td>
                <td className="px-4 py-2">{s.supplier_group}</td><td className="px-4 py-2">{s.country}</td>
                <td className="px-4 py-2 text-xs">{s.qualifications.length ? s.qualifications.map((qq) => `${qq.item_group} · ${t(`p2p.qstatus.${qq.status}`, qq.status)}`).join(", ") : <span className="text-status-amber">{t("p2p.directory.unqualified")}</span>}</td>
                <td className="px-4 py-2 text-xs">{s.disabled ? t("p2p.directory.disabled") : s.on_hold ? <span className="text-status-red">{t("p2p.directory.onHold")}</span> : s.open_file ? <Link to={`/procure/onboarding/${encodeURIComponent(s.open_file.name)}`} className="text-brand-primary hover:underline">{t("p2p.directory.fileOpen", { status: s.open_file.status })}</Link> : "✓"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
